/**
 * Episode Manager Component
 * Panel for managing episodes in the Organize view
 */
import React from 'react';
import { Plus, ChevronUp, ChevronDown, X, Layers } from 'lucide-react';

export function EpisodeManager({
  episodes,
  activeEpisode,
  setActiveEpisode,
  addEpisode,
  updateEpisodeName,
  workspace,
  addToEpisode,
  activeEpisodeData,
  reorderPanel,
  removeFromEpisode
}) {
  return (
    <div className="grid lg:grid-cols-4 gap-6">
      {/* Left: Episode List */}
      <div className="lg:col-span-1">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-teko text-lg">Episodes</h3>
            <button
              onClick={addEpisode}
              className="p-2 bg-[var(--primary)] text-white rounded-lg hover:bg-[var(--primary)]/80"
              data-testid="add-episode-btn"
            >
              <Plus size={16} />
            </button>
          </div>
          
          <div className="space-y-2">
            {episodes.map(ep => (
              <button
                key={ep.id}
                onClick={() => setActiveEpisode(ep.id)}
                className={`w-full text-left p-3 rounded-lg transition-colors ${
                  activeEpisode === ep.id
                    ? 'bg-[var(--primary)] text-white'
                    : 'bg-[var(--background)] hover:bg-[var(--background)]/80'
                }`}
                data-testid={`episode-${ep.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{ep.name}</span>
                  <span className="text-sm opacity-70">{ep.panels.length} panels</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Available Images */}
        <div className="mt-4 bg-[var(--surface)] border border-[var(--border)] rounded-xl p-4">
          <h3 className="font-teko text-lg mb-3">Available Images</h3>
          <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            {workspace.map(img => (
              <button
                key={img.id}
                onClick={() => addToEpisode(img.id, activeEpisode)}
                className="aspect-square rounded overflow-hidden border border-[var(--border)] hover:border-[var(--primary)] transition-colors"
                data-testid={`add-image-${img.id}`}
              >
                <img src={img.preview} alt={img.name} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Right: Episode Panels */}
      <div className="lg:col-span-3">
        <div className="bg-[var(--surface)] border border-[var(--border)] rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <input
              type="text"
              value={activeEpisodeData?.name || ''}
              onChange={(e) => updateEpisodeName(activeEpisode, e.target.value)}
              className="text-xl font-teko bg-transparent border-b border-transparent hover:border-[var(--border)] focus:border-[var(--primary)] outline-none"
              data-testid="episode-name-input"
            />
            <span className="text-[var(--text-secondary)]">{activeEpisodeData?.panels.length || 0} panels</span>
          </div>

          {activeEpisodeData?.panels.length === 0 ? (
            <div className="text-center py-12 border-2 border-dashed border-[var(--border)] rounded-lg">
              <Layers size={48} className="mx-auto text-[var(--text-secondary)] mb-4" />
              <p className="text-[var(--text-secondary)]">No panels in this episode</p>
              <p className="text-sm text-[var(--text-secondary)] mt-1">Click images on the left to add them</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {activeEpisodeData?.panels.map((panel, index) => (
                <div
                  key={panel.id}
                  className="relative group bg-[var(--background)] border border-[var(--border)] rounded-lg overflow-hidden"
                >
                  <div className="aspect-[3/4] relative">
                    <img src={panel.preview} alt={panel.name} className="w-full h-full object-cover" />
                    <div className="absolute top-2 left-2 w-8 h-8 bg-[var(--primary)] text-white rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center justify-center gap-2">
                      <button
                        onClick={() => reorderPanel(activeEpisode, panel.id, 'up')}
                        disabled={index === 0}
                        className="p-1 bg-white/20 rounded hover:bg-white/30 disabled:opacity-30"
                        data-testid={`panel-${panel.id}-up`}
                      >
                        <ChevronUp size={16} />
                      </button>
                      <button
                        onClick={() => reorderPanel(activeEpisode, panel.id, 'down')}
                        disabled={index === activeEpisodeData.panels.length - 1}
                        className="p-1 bg-white/20 rounded hover:bg-white/30 disabled:opacity-30"
                        data-testid={`panel-${panel.id}-down`}
                      >
                        <ChevronDown size={16} />
                      </button>
                      <button
                        onClick={() => removeFromEpisode(panel.id, activeEpisode)}
                        className="p-1 bg-red-500/80 rounded hover:bg-red-500"
                        data-testid={`panel-${panel.id}-remove`}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default EpisodeManager;
